import React, { createContext, useState, useContext, useEffect, useMemo } from "react";
import axios from "axios";
import { getToken } from "../utils/tokenUtils";

// Create the EmployeeApproverMasterContext
const EmployeeApproverMasterContext = createContext();

// Custom hook to use the EmployeeApproverMasterContext
export const useEmployeeApprover = () => useContext(EmployeeApproverMasterContext);

// Provider component to wrap the app
export const EmployeeApproverMasterProvider = ({ children }) => {
  const [employeeApprovers, setEmployeeApprovers] = useState([]);
  const API_URL = "https://hrmspayroll.prepstripe.com/api/employee-approvers"; 
  const token = getToken();
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 15,
  });
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchEmployeeApprovers = async (page = 1) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}?page=${page}`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      const responseData = response.data.data;
      
      // Handle both paginated and non-paginated responses
      if (Array.isArray(responseData)) {
        // Direct array response
        setEmployeeApprovers(responseData);
        setPagination({
          currentPage: 1,
          totalPages: 1,
          totalItems: responseData.length,
          perPage: responseData.length || 15,
        });
      } else if (responseData && Array.isArray(responseData.data)) {
        // Paginated structure
        setEmployeeApprovers(responseData.data);
        setPagination({
          currentPage: responseData.current_page || 1,
          totalPages: responseData.last_page || 1,
          totalItems: responseData.total || 0,
          perPage: responseData.per_page || 15,
        });
      } else {
        setEmployeeApprovers([]);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching employee approve module master:", error);
      setError(error.response?.data?.message || "Failed to fetch employee approver.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch employee approver master from the API on load
  useEffect(() => {
    fetchEmployeeApprovers();
  }, [API_URL, token]);

  // Add a new employee approver
  const addEmployeeApprover = async (newEmployeeApprover) => {
    try {
      const response = await axios.post(API_URL, newEmployeeApprover, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      setEmployeeApprovers((prev) => [response.data?.data, ...prev]);
    } catch (error) {
      console.error("Error adding employee approver module:", error);
    }
  };

  // Update an existing employee approver
  const updateEmployeeApprover = async (updatedEmployeeApprover) => {
    try {
      const response = await axios.put(`${API_URL}/${updatedEmployeeApprover.id}`, updatedEmployeeApprover, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      setEmployeeApprovers((prev) =>
        prev.map((employeeApprover) =>
          employeeApprover.id === updatedEmployeeApprover.id ? { ...employeeApprover, ...response.data?.data } : employeeApprover
        )
      );
    } catch (error) {
      console.error("Error updating employee approver master:", error);
    }
  };

  // Delete a employee approver
  const deleteEmployeeApprover = async (id) => {
    try {
      await axios.delete(`${API_URL}/${id}`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      setEmployeeApprovers((prev) => prev.filter((employeeApprover) => employeeApprover.id !== id));
    } catch (error) {
      console.error("Error deleting employee approver:", error);
    }
  };

  // Memoize the context value
  const value = useMemo(
    () => ({
      isLoading,
      error,
      pagination,
      employeeApprovers,
      fetchEmployeeApprovers,
      addEmployeeApprover,
      updateEmployeeApprover,
      deleteEmployeeApprover,
    }),
    [employeeApprovers, isLoading] 
  );

  return <EmployeeApproverMasterContext.Provider value={value}>{children}</EmployeeApproverMasterContext.Provider>;
};
